/**
 * KI-Interview nach der Eintragung (Speech-to-Text-Ticket).
 *
 * Rendert INLINE im selben Container wie der restliche Funnel/OTP-Flow
 * (kein Overlay) — eingehängt in OtpFlow vor der Danke-Seite. Ziel: maximal
 * viele Projekt-Infos bei minimaler Hürde:
 * - Chip-Fragen: nur antippen (Freitext optional zuschaltbar)
 * - Offene Fragen: großer Sprech-Button mit Live-Transkript (Audio bleibt
 *   im Browser), Text vor dem Senden korrigierbar, Box wächst mit
 * - Jede Antwort sofort gespeichert; Überspringen jederzeit ohne Verlust
 */
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  startInterview,
  answerInterview,
  skipInterview,
  transcribeAudio,
  reportVoiceEvent,
  INTERVIEW_STATUS,
  INTERVIEW_INPUT_MODE
} from "../../services/aiInterviewApi";

const C = {
  ink: "#2A2724",
  muted: "#6B6358",
  line: "#E5E0D6",
  soft: "#F5F2EC",
  accent: "#C8623D",
  accentSoft: "#F9EAE3",
  green: "#2D5F3F",
  greenSoft: "#E7F0EA",
  danger: "#B8442E"
};

const Wrap = styled.div`
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  padding: 20px 18px 24px;
  background: #fff;
  border-radius: 16px;
  box-sizing: border-box;
  text-align: left;
  font-family: inherit;
  color: ${C.ink};
  * {
    box-sizing: border-box;
  }
`;
const Loading = styled.div`
  text-align: center;
  padding: 48px 12px;
  color: ${C.muted};
  font-size: 15px;
  &::before {
    content: "";
    display: block;
    width: 34px;
    height: 34px;
    margin: 0 auto 14px;
    border: 3px solid ${C.line};
    border-top-color: ${C.accent};
    border-radius: 50%;
    animation: aiSpin 0.9s linear infinite;
  }
  @keyframes aiSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Head = styled.div`
  text-align: center;
  margin-bottom: 14px;
  h3 {
    font-size: 21px;
    font-weight: 700;
    margin: 0 0 4px;
  }
  p {
    font-size: 14px;
    color: ${C.muted};
    margin: 0;
  }
`;
const ProgressLabel = styled.div`
  font-size: 13px;
  color: ${C.muted};
  margin-bottom: 5px;
`;
const ProgressBar = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 16px;
  span {
    flex: 1;
    height: 5px;
    border-radius: 3px;
    background: ${C.line};
  }
  span.done {
    background: ${C.accent};
  }
`;
const Bubble = styled.div`
  background: ${({ $user }) => ($user ? C.accentSoft : C.soft)};
  border-radius: ${({ $user }) =>
    $user ? "14px 14px 4px 14px" : "14px 14px 14px 4px"};
  padding: 12px 15px;
  margin: ${({ $user }) => ($user ? "8px 0 8px 28px" : "8px 28px 8px 0")};
  max-width: 100%;
  overflow-wrap: break-word;
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin: 16px 0 6px;
`;
const Chip = styled.button`
  border: 1.5px solid ${C.line};
  background: #fff;
  border-radius: 22px;
  padding: 11px 16px;
  max-width: 100%;
  white-space: normal;
  font-size: 14.5px;
  cursor: pointer;
  color: ${C.ink};
  transition: all 0.15s;
  &:hover {
    border-color: ${C.accent};
    color: ${C.accent};
    background: ${C.accentSoft};
  }
`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(184,68,46,0.35); }
  70% { box-shadow: 0 0 0 18px rgba(184,68,46,0); }
  100% { box-shadow: 0 0 0 0 rgba(184,68,46,0); }
`;
const MicArea = styled.div`
  text-align: center;
  margin: 16px 0 4px;
`;
const MicButton = styled.button`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: none;
  background: ${({ $rec }) => ($rec ? C.danger : C.accent)};
  color: #fff;
  font-size: 32px;
  cursor: pointer;
  animation: ${({ $rec }) => ($rec ? pulse : "none")} 1.6s infinite;
`;
const MicLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-top: 10px;
`;
const MicSub = styled.div`
  font-size: 13px;
  color: ${C.muted};
  margin-top: 2px;
`;
const AnswerBox = styled.div`
  margin-top: 14px;
  textarea {
    width: 100%;
    border: 1.5px solid ${({ $active }) => ($active ? C.accent : C.line)};
    border-radius: 12px;
    padding: 12px 14px;
    font-size: 16px;
    line-height: 1.5;
    font-family: inherit;
    resize: none;
    overflow-y: auto;
    min-height: 52px;
    max-height: 180px;
    outline: none;
    transition: border-color 0.15s;
  }
`;
const BoxHint = styled.div`
  font-size: 12.5px;
  color: ${C.muted};
  margin: 4px 2px 8px;
`;
const SendButton = styled.button`
  width: 100%;
  border: none;
  background: ${({ disabled }) => (disabled ? "#C9C4BA" : C.green)};
  color: #fff;
  border-radius: 12px;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
`;
const TextToggle = styled.button`
  display: block;
  margin: 10px auto 0;
  background: none;
  border: none;
  color: ${C.muted};
  font-size: 13.5px;
  text-decoration: underline;
  cursor: pointer;
`;
const SkipLink = styled.button`
  display: block;
  margin: 18px auto 0;
  background: none;
  border: none;
  color: ${C.muted};
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
`;
const VoiceHint = styled.div`
  background: #fdf3ee;
  border: 1px solid #f0d7c9;
  border-radius: 10px;
  padding: 10px 13px;
  margin-top: 12px;
  font-size: 13.5px;
  line-height: 1.45;
  color: ${C.ink};
`;
const Privacy = styled.div`
  font-size: 12px;
  color: ${C.muted};
  margin-top: 14px;
  line-height: 1.45;
  text-align: center;
`;
const SummaryBox = styled.div`
  background: ${C.greenSoft};
  border-radius: 12px;
  padding: 16px 18px;
  margin-top: 12px;
  div {
    font-size: 14.5px;
    margin-bottom: 6px;
  }
  strong {
    color: ${C.green};
  }
`;

const SpeechRecognition =
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
// In-App-Browser (Facebook, Instagram, TikTok …): Dort ist die Browser-
// Spracherkennung meist nicht funktionsfähig, obwohl die API existiert.
// Relevant, weil ein Großteil der Leads aus Social-Anzeigen kommt.
const IS_IN_APP_BROWSER =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok|Snapchat|Twitter|Pinterest|GSA\//i.test(
    ua
  );
const IS_IOS =
  /iPad|iPhone|iPod/.test(ua) ||
  (typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1);
const HAS_MEDIA_RECORDER =
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined" &&
  !!navigator?.mediaDevices?.getUserMedia;

// Eingabeart pro Gerät:
// - "speech": Browser-Spracherkennung, Live-Transkript, Audio bleibt lokal
//   (Desktop, Android-Chrome — schnell und kostenlos)
// - "recorder": Aufnahme + serverseitige Transkription (iOS und In-App-
//   Browser, wo die Spracherkennung an Systemeinstellungen scheitert)
// - "none": keine Spracheingabe möglich → sauber gestaltete Texteingabe
function pickVoiceMode() {
  // Test-/Support-Schalter: ?voice=recorder|speech|none erzwingt eine
  // Eingabeart — so lässt sich der iOS-Pfad auch am Desktop prüfen.
  const forced =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("voice");
  if (["speech", "recorder", "none"].includes(forced)) return forced;

  if (SpeechRecognition && !IS_IOS && !IS_IN_APP_BROWSER) return "speech";
  if (HAS_MEDIA_RECORDER) return "recorder";
  if (SpeechRecognition) return "speech";
  return "none";
}

// Aufnahmeformat, das der jeweilige Browser wirklich kann (iOS: mp4, sonst webm)
function pickAudioMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/aac",
    "audio/ogg;codecs=opus"
  ];
  return (
    candidates.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || ""
  );
}

// Klartext-Hilfe statt stiller Abbruch — je nachdem, woran es lag.
function micHelpText(reason) {
  switch (reason) {
    case "not-allowed":
    case "NotAllowedError":
      return IS_IOS
        ? "Das Mikrofon ist für diese Seite gesperrt. Sie können es in den iPhone-Einstellungen unter „Safari → Mikrofon“ erlauben — oder Ihre Antwort einfach eintippen."
        : "Das Mikrofon ist für diese Seite gesperrt. Sie können es links in der Adressleiste Ihres Browsers erlauben — oder Ihre Antwort einfach eintippen.";
    case "service-not-allowed":
      return "Die Spracherkennung ist auf diesem Gerät deaktiviert (auf dem iPhone: Einstellungen → Allgemein → Tastatur → Diktierfunktion). Tippen Sie Ihre Antwort gern einfach ein.";
    case "NotFoundError":
    case "audio-capture":
      return "Wir konnten kein Mikrofon finden. Tippen Sie Ihre Antwort einfach ein.";
    case "no-speech":
      return "Wir haben leider nichts gehört. Versuchen Sie es noch einmal — oder tippen Sie Ihre Antwort ein.";
    case "network":
      return "Die Spracherkennung ist gerade nicht erreichbar. Tippen Sie Ihre Antwort einfach ein.";
    case "transcribe-failed":
      return "Die Aufnahme konnte nicht umgewandelt werden. Versuchen Sie es noch einmal — oder tippen Sie Ihre Antwort ein.";
    default:
      return "Die Spracheingabe funktioniert auf diesem Gerät gerade nicht. Tippen Sie Ihre Antwort einfach ein — Stichworte reichen völlig.";
  }
}

const AiInterview = ({ leadId, onFinish }) => {
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [showTextForChips, setShowTextForChips] = useState(false);
  const [error, setError] = useState(null);
  // Spracheingabe: gewählter Modus, laufende Transkription, Klartext-Hinweis
  const [voiceMode, setVoiceMode] = useState(() => pickVoiceMode());
  const [voiceHint, setVoiceHint] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  // True while the text in the answer box came from speech (live recognition
  // or server transcription). The answer is usually sent after the recording
  // has stopped, so `recording` alone would store every spoken answer as text.
  const [fromVoice, setFromVoice] = useState(false);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const textareaRef = useRef(null);
  const bottomRef = useRef(null);

  const pushBot = (s) => {
    const parts = [];
    if (s.ack) parts.push({ who: "bot", text: s.ack });
    if (s.question) parts.push({ who: "bot", text: s.question });
    if (parts.length) setMessages((m) => [...m, ...parts]);
  };

  useEffect(() => {
    // Sichtbar oben starten (der OTP-Screen davor kann gescrollt sein).
    window.scrollTo({ top: 0 });
    let cancelled = false;
    // Die Lead-Anlage ist asynchron (Raw Lead → Hintergrund-Job →
    // LeadResponse): Direkt nach dem Absenden kann /start noch 404 liefern,
    // obwohl der Lead Sekunden später existiert. Deshalb mit Backoff
    // nachversuchen — und Fehler IMMER loggen statt still zur Danke-Seite
    // zu springen (Dev-Vorfall 12.08.: unsichtbarer Ausfall, siehe Ticket).
    // Fenster ~48 s: Die Worker-Materialisierung wurde auf dev wiederholt
    // mit 20 s+ gemessen (Analyse 20.08.); währenddessen läuft die
    // Lade-Ansicht („Ihre Anfrage wird vorbereitet …"). Feine Staffelung
    // statt großer Sprünge: Sobald der Lead existiert, startet das Interview
    // beim NÄCHSTEN Versuch — mit 10–15-s-Lücken wartete der Nutzer sonst
    // deutlich länger als nötig (Sarhads gefilterter Lead, 01.09.).
    const RETRY_DELAYS_MS = [
      1500, 2000, 2500, 3000, 3000, 4000, 4000, 5000, 5000, 6000, 6000, 6000
    ];
    (async () => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          const s = await startInterview(leadId);
          if (cancelled) return;
          setState(s);
          if (s.status === INTERVIEW_STATUS.ACTIVE) pushBot(s);
          return;
        } catch (e) {
          if (cancelled) return;
          const delay = RETRY_DELAYS_MS[attempt];
          // eslint-disable-next-line no-console
          console.error(
            `[AIInterview] start failed (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length + 1})`,
            e?.response?.status || e?.message
          );
          if (delay == null) {
            setError(true);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    // BEWUSST ohne behavior:"smooth": Auf dem verschachtelten Scroll-Container
    // des Funnels bewirkt der Smooth-Aufruf in Chrome NICHTS (scrollTop bleibt
    // stehen) — der Auto-Scroll war damit komplett wirkungslos und der
    // Absenden-Button blieb nach jeder Frage unter der Falz (Marvins Test,
    // 19.08.). Der instantane Aufruf scrollt zuverlässig.
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages, state, busy, showTextForChips]);

  // Textbox wächst mit dem Inhalt (wichtig bei langen Sprachantworten).
  const autogrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  };
  useEffect(autogrow, [input]);

  const stopMic = () => {
    // onresult hart abklemmen: sonst schreiben nachlaufende Events das
    // alte Transkript zurück in die (bereits geleerte) Box der nächsten Frage.
    if (recognitionRef.current) {
      recognitionRef.current.onresult = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    // Aufnahme beenden → onstop startet die Transkription
    if (mediaRecorderRef.current) {
      try {
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        /* Recorder war bereits beendet */
      }
      mediaRecorderRef.current = null;
    }
    setRecording(false);
  };

  // Aufräumen, wenn der Nutzer die Seite verlässt: Mikrofon freigeben.
  useEffect(
    () => () => {
      try {
        recognitionRef.current?.stop();
        if (mediaRecorderRef.current?.state !== "inactive") {
          mediaRecorderRef.current?.stop();
        }
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
      } catch (e) {
        /* egal beim Unmount */
      }
    },
    []
  );

  // Input mode of a typed or spoken answer (chips send CHIP themselves).
  const textInputMode = () =>
    recording || fromVoice
      ? INTERVIEW_INPUT_MODE.VOICE
      : INTERVIEW_INPUT_MODE.TEXT;

  const send = async (answer, inputMode) => {
    if (!answer?.trim() || busy) return;
    if (recording) stopMic();
    setBusy(true);
    setInput("");
    setFromVoice(false);
    setShowTextForChips(false);
    setMessages((m) => [...m, { who: "user", text: answer.trim() }]);
    try {
      const s = await answerInterview(leadId, answer.trim(), inputMode);
      setState(s);
      if (s.status === INTERVIEW_STATUS.ACTIVE) pushBot(s);
    } catch (e) {
      setError(true);
    }
    setBusy(false);
  };

  // Spracheingabe scheitert nie stumm: Hinweis zeigen, auf Tippen umschalten,
  // Ursache ans Backend melden (Telemetrie).
  const failVoice = (reason, { disable } = {}) => {
    setVoiceHint(micHelpText(reason));
    setRecording(false);
    setTranscribing(false);
    if (disable) setVoiceMode("none");
    reportVoiceEvent(
      leadId,
      disable ? "voice_disabled" : "voice_error",
      reason
    );
    setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 50);
  };

  // Expliziter Freigabe-Dialog VOR jeder Aufnahme. Genau das fehlte bisher:
  // Ohne getUserMedia erscheint der Berechtigungs-Dialog auf manchen Geräten
  // (vor allem iOS) gar nicht, und die Spracherkennung bricht still ab.
  const requestMicStream = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      failVoice("unsupported", { disable: true });
      return null;
    }
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      // NotAllowedError = abgelehnt oder vom System gesperrt: Ein erneuter
      // Dialog erscheint dann nicht mehr, deshalb dauerhaft auf Text.
      failVoice(e.name || "NotAllowedError", {
        disable: e.name === "NotAllowedError" || e.name === "NotFoundError"
      });
      return null;
    }
  };

  // Variante A: Browser-Spracherkennung mit Live-Transkript (Audio bleibt lokal)
  const startSpeechRecognition = async () => {
    const stream = await requestMicStream();
    if (!stream) return;
    // Der Stream diente nur dazu, die Freigabe zu erzwingen — die Erkennung
    // öffnet ihr eigenes Mikrofon.
    stream.getTracks().forEach((t) => t.stop());

    const rec = new SpeechRecognition();
    rec.lang = "de-DE";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (event) => {
      let text = "";
      for (const res of event.results) text += res[0].transcript;
      setInput(text);
      if (text.trim()) setFromVoice(true);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = (event) => {
      const reason = event.error;
      if (reason === "aborted") return; // vom Nutzer gestoppt
      // Systemseitige Sperren (Diktat aus, Berechtigung entzogen): einmalig
      // auf die Server-Transkription ausweichen, falls möglich.
      if (
        (reason === "service-not-allowed" || reason === "not-allowed") &&
        HAS_MEDIA_RECORDER
      ) {
        setVoiceMode("recorder");
        setVoiceHint(null);
        reportVoiceEvent(leadId, "fallback_to_recorder", reason);
        setRecording(false);
        return;
      }
      failVoice(reason, { disable: reason === "audio-capture" });
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setRecording(true);
      setVoiceHint(null);
      reportVoiceEvent(leadId, "voice_started", "speech");
    } catch (e) {
      recognitionRef.current = null;
      failVoice(e.name || "start-failed");
    }
  };

  // Variante B: echte Aufnahme + serverseitige Transkription. Braucht nur die
  // Mikrofon-Freigabe (nicht Apples Diktat-Dienst) und funktioniert daher
  // dort, wo die Browser-Spracherkennung scheitert.
  const startRecorder = async () => {
    const stream = await requestMicStream();
    if (!stream) return;
    try {
      const mimeType = pickAudioMime();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, {
          type: mimeType || "audio/webm"
        });
        chunksRef.current = [];
        if (!blob.size) {
          failVoice("no-speech");
          return;
        }
        setTranscribing(true);
        try {
          const ext = (mimeType || "audio/webm").includes("mp4")
            ? "mp4"
            : "webm";
          const { text } = await transcribeAudio(
            leadId,
            blob,
            `aufnahme.${ext}`
          );
          setTranscribing(false);
          if (text) {
            setInput((prev) => (prev ? `${prev} ${text}` : text));
            setFromVoice(true);
            setVoiceHint(null);
            reportVoiceEvent(leadId, "transcribe_ok", `${blob.size}`);
          } else {
            failVoice("no-speech");
          }
        } catch (e) {
          setTranscribing(false);
          failVoice("transcribe-failed");
        }
      };
      streamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setVoiceHint(null);
      reportVoiceEvent(leadId, "voice_started", "recorder");
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      failVoice(e.name || "recorder-failed", { disable: true });
    }
  };

  const toggleMic = async () => {
    if (recording) {
      stopMic();
      // Kein Auto-Fokus auf Touch-Geräten: Das öffnete nach jedem Aufnahme-
      // Stopp die Tastatur und verdeckte den Dialog (Sarhads Test 24.08.).
      // Am Desktop bleibt der Fokus — dort ist er zum Nachbessern praktisch.
      const isTouch =
        typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (!isTouch) textareaRef.current?.focus({ preventScroll: true });
      return;
    }
    if (voiceMode === "speech") return startSpeechRecognition();
    if (voiceMode === "recorder") return startRecorder();
  };

  const skip = async () => {
    if (recording) stopMic();
    setBusy(true);
    try {
      const s = await skipInterview(leadId);
      // Auch ohne onFinish (Dev-Einstieg) sofort den Abschluss-Zustand
      // zeigen — nie auf derselben Frage stehen bleiben.
      setState(s);
    } catch (e) {
      /* Überspringen darf nie blockieren */
    }
    setBusy(false);
    onFinish?.("skipped");
  };

  // Fehler dürfen den Hausbesitzer nie festhalten — direkt weiter zur Danke-Seite.
  useEffect(() => {
    if (error) onFinish?.("error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (error) return null;
  // Der Interview-Start braucht 1–3 s (LLM plant die Fragen) — nie einen
  // leeren weißen Container zeigen, sondern einen erklärenden Lade-Zustand.
  if (!state)
    return (
      <Wrap>
        <Loading>Ihre Anfrage wird für den Fachbetrieb vorbereitet …</Loading>
      </Wrap>
    );

  const done =
    state.status === INTERVIEW_STATUS.COMPLETED ||
    state.status === INTERVIEW_STATUS.SKIPPED;
  const total = state.plannedQuestions || 0;
  const idx = Math.min(state.questionIndex, total);
  const isChipQuestion = !!state.chips && !state.open;
  const canUseVoice = state.open && voiceMode !== "none";
  const isRecorderMode = voiceMode === "recorder";
  const showTextarea = !isChipQuestion || showTextForChips;

  return (
    <Wrap>
      {!done && total > 0 && (
        <>
          <Head>
            <h3>Fast geschafft!</h3>
            <p>
              Noch {Math.max(total - idx + 1, 1)} kurze{" "}
              {total - idx + 1 === 1 ? "Frage" : "Fragen"} — damit Ihr
              Fachbetrieb perfekt vorbereitet ist. Dauert unter einer Minute.
            </p>
          </Head>
          <ProgressLabel>
            Frage {idx} von {total}
          </ProgressLabel>
          <ProgressBar>
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={i < idx ? "done" : ""} />
            ))}
          </ProgressBar>
        </>
      )}

      {messages.map((m, i) => (
        <Bubble key={i} $user={m.who === "user"}>
          {m.text}
        </Bubble>
      ))}
      {busy && <Bubble>Einen Moment …</Bubble>}

      {done ? (
        <>
          {state.summary?.length > 0 && (
            <SummaryBox>
              <div style={{ marginBottom: 10, fontWeight: 700 }}>
                Das geben wir Ihrem Fachbetrieb mit:
              </div>
              {state.summary.map((s, i) => (
                <div key={i}>
                  <strong>{s.label}:</strong> {s.value}
                </div>
              ))}
            </SummaryBox>
          )}
          {onFinish && (
            <SendButton
              style={{ marginTop: 14 }}
              onClick={() => onFinish("done")}
            >
              Alles korrekt — abschließen
            </SendButton>
          )}
        </>
      ) : (
        !busy && (
          <>
            {isChipQuestion && (
              <>
                <Chips>
                  {state.chips.map((c) => (
                    <Chip
                      key={c}
                      onClick={() => send(c, INTERVIEW_INPUT_MODE.CHIP)}
                    >
                      {c}
                    </Chip>
                  ))}
                </Chips>
                {!showTextForChips && (
                  <TextToggle onClick={() => setShowTextForChips(true)}>
                    Lieber selbst formulieren
                  </TextToggle>
                )}
              </>
            )}

            {canUseVoice && (
              <MicArea>
                <MicButton
                  $rec={recording}
                  onClick={toggleMic}
                  disabled={transcribing}
                  aria-label="Spracheingabe starten oder beenden"
                >
                  {recording ? "◼" : "🎤"}
                </MicButton>
                <MicLabel>
                  {transcribing
                    ? "Einen Moment — wir schreiben Ihre Antwort auf …"
                    : recording
                      ? "Antippen & Aufnahme stoppen"
                      : "Antippen & frei drauflos sprechen"}
                </MicLabel>
                <MicSub>
                  {transcribing
                    ? "Das dauert nur wenige Sekunden."
                    : recording
                      ? isRecorderMode
                        ? "Ich höre zu — sprechen Sie einfach. Nach dem Stoppen erscheint Ihr Text."
                        : "Ich höre zu — sprechen Sie einfach."
                      : "Ihre Worte erscheinen als Text — oder unten eintippen"}
                </MicSub>
              </MicArea>
            )}
            {voiceHint && <VoiceHint>{voiceHint}</VoiceHint>}

            {showTextarea && (
              <AnswerBox $active={recording || !!input}>
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={input}
                  placeholder={
                    canUseVoice
                      ? "Hier erscheint Ihre gesprochene Antwort …"
                      : "Ihre Antwort — Stichworte reichen völlig …"
                  }
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Box cleared: whatever comes next is typed.
                    if (!e.target.value.trim()) setFromVoice(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send(input, textInputMode());
                    }
                  }}
                />
                {input && (
                  <BoxHint>
                    Kurz prüfen und bei Bedarf anpassen — dann absenden.
                  </BoxHint>
                )}
                <SendButton
                  disabled={!input.trim()}
                  onClick={() => send(input, textInputMode())}
                >
                  Antwort absenden
                </SendButton>
              </AnswerBox>
            )}

            <SkipLink onClick={skip}>
              Fragen überspringen &amp; Anfrage direkt absenden
            </SkipLink>
            {canUseVoice && (
              <Privacy>
                {isRecorderMode
                  ? "Ihre Aufnahme wird nur in Text umgewandelt und nicht gespeichert."
                  : "Ihre Stimme wird direkt auf Ihrem Gerät in Text umgewandelt und nicht als Audio übertragen."}{" "}
                Bitte nennen Sie keine sensiblen persönlichen Daten (z.&nbsp;B.
                Gesundheitsdaten).
              </Privacy>
            )}
          </>
        )
      )}
      {/* Scroll-Anker BEWUSST hinter dem Eingabebereich: Nach jeder neuen
          Frage muss der Absenden-Button im Sichtfeld landen — stand der
          Anker über dem Eingabebereich, blieben Mikro/Textfeld/Button unter
          der Falz (Marvins Test, 19.08.). */}
      <div ref={bottomRef} />
    </Wrap>
  );
};

export default AiInterview;
